import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarPlus, Pencil, Ban, Upload, CheckCheck, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { addressLine, addressExtra, fmtDate, fmtDateTime, SLOTS, fmtEuro, STATUS, OUTCOME_LABEL } from "@/lib/format";
import { Field } from "@/components/Common";
import { StatusBadge, PriorityBadge, TypeBadge, SlaChip } from "@/components/StatusBadge";
import { OrderFormDialog } from "@/components/OrderFormDialog";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { ReportView, AttachmentGrid } from "@/components/ReportView";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Section = ({ title, children }) => (
  <div className="border bg-white p-6"><div className="micro-label mb-4">{title}</div>{children}</div>
);

const FLOW = ["RECEIVED", "TO_PLAN", "PLANNED", "IN_PROGRESS", "COMPLETED", "TO_VALIDATE", "VALIDATED", "BILLABLE", "CLOSED"];

const Lifecycle = ({ status }) => {
  const idx = FLOW.indexOf(status);
  return (
    <div className="mb-6 flex overflow-x-auto border bg-white" data-testid="order-lifecycle">
      {FLOW.map((s, i) => (
        <div key={s} className={`flex-1 whitespace-nowrap border-r px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider last:border-0 ${s === status ? "bg-[#0B0F14] text-white" : i < idx ? "text-primary" : "text-muted-foreground"}`}>{STATUS[s].label}</div>
      ))}
      {["SUSPENDED", "CANCELLED"].includes(status) && <div className="whitespace-nowrap bg-violet-600 px-3 py-2 text-[11px] font-semibold uppercase text-white">{STATUS[status].label}</div>}
    </div>
  );
};

const Technical = ({ o }) => o.service_type !== "ASSURANCE" ? (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
    <Field label="Tipo attivazione" value={o.delivery?.activation_type} /><Field label="Tecnologia" value={o.technology} />
    <Field label="Codice ordine OLO" value={o.delivery?.network_code} mono /><Field label="ROE / PTE" value={o.delivery?.roe} mono />
    <Field label="PFS / Armadio" value={o.delivery?.port_splitter} mono /><Field label="PTO" value={o.delivery?.pto} mono />
    <Field label="POP" value={o.delivery?.pop} mono /><Field label="ID Risorsa" value={o.delivery?.resource_id} mono />
    <Field label="ID Building" value={o.delivery?.building_id} mono /><Field label="Materiale / Psw apparato" value={o.delivery?.equipment} mono />
    <Field label="Appuntamento" value={[fmtDate(o.appointment_date), o.delivery?.appointment_time].filter((x) => x && x !== "—").join(" · ")} />
    <Field label="Anticipo / Posticipo" value={o.delivery?.time_flex} />
    <Extra d={o.delivery} />
  </div>
) : (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
    <Field label="Sintomo" value={o.assurance?.symptom} /><Field label="Tecnologia" value={o.technology} />
    <Field label="Data apertura" value={fmtDateTime(o.assurance?.opened_at)} /><Field label="Scadenza SLA" value={fmtDateTime(o.sla_due_at)} mono />
    <Field label="ID Risorsa" value={o.delivery?.resource_id} mono /><Field label="POP" value={o.delivery?.pop} mono />
    <Field label="PFS / Armadio" value={o.delivery?.port_splitter} mono /><Field label="ROE / PTE" value={o.delivery?.roe} mono />
    <Field label="ID Building" value={o.delivery?.building_id} mono />
    <Field label="Appuntamento" value={[fmtDate(o.appointment_date), o.delivery?.appointment_time].filter((x) => x && x !== "—").join(" · ")} />
    <Extra d={o.delivery} />
  </div>
);

const EXTRA = [["gpon", "Gpon attestazione"], ["bretellaggio", "Bretellaggio"], ["olo", "OLO / Operatore"], ["cluster", "Cluster"], ["characterization", "Caratterizzazione"]];
const Extra = ({ d }) => EXTRA.filter(([k]) => d?.[k]).map(([k, l]) => <Field key={k} label={l} value={d[k]} mono />);

const Assignments = ({ list, nameOf }) => (
  <div className="overflow-x-auto border bg-white" data-testid="order-assignments">
    {list.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nessuna assegnazione</div>}
    {list.map((a) => (
      <div key={a.id} className={`grid grid-cols-2 gap-3 border-b px-6 py-3 text-sm last:border-0 md:grid-cols-6 ${a.active ? "bg-accent/40" : ""}`}>
        <Field label="Squadra" value={nameOf("teams", a.team_id)} /><Field label="Tecnico" value={nameOf("employees", a.employee_id)} />
        <Field label="Datore di lavoro" value={nameOf("companies", a.employer_company_id, "code")} /><Field label="Società esecutrice" value={nameOf("companies", a.executing_company_id, "code")} />
        <Field label="Data / fascia" value={`${fmtDate(a.scheduled_date)} · ${SLOTS[a.slot]}`} />
        <Field label="Stato" value={a.active ? `Attiva (da ${a.assigned_by})` : `Chiusa: ${a.ended_reason}`} />
      </div>
    ))}
  </div>
);

const Economics = ({ o }) => (
  <Section title="Valorizzazione e costi">
    <div className="space-y-1 text-sm">
      {(o.production || []).map((p) => <div key={p.id} className="flex justify-between font-mono text-xs"><span>{p.stage} · {p.price_code} {p.description} × {p.qty}</span><span>{fmtEuro(p.amount)}</span></div>)}
      {(o.costs || []).map((c) => <div key={c.id} className="flex justify-between font-mono text-xs text-red-700"><span>{c.kind === "MATERIAL" ? `Materiale ${c.item_code} · ${c.qty} × ${fmtEuro(c.unit_cost)}` : c.kind === "VEHICLE" ? `Mezzo ${c.plate} · quota ${c.month}` : `Manodopera · ${c.hours}h × ${fmtEuro(c.unit_cost)}`}</span><span>-{fmtEuro(c.amount)}</span></div>)}
      {!o.production?.length && !o.costs?.length && <div className="text-muted-foreground">Nessun movimento economico</div>}
    </div>
  </Section>
);

const useActions = (id, load) => {
  const run = async (path, body, msg) => {
    try {
      await api.post(`/orders/${id}/${path}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return {
    verify: () => run("verify", {}, "Fattibilità verificata: ordine da pianificare"),
    cancel: () => { const r = window.prompt("Motivazione annullamento"); if (r) run("cancel", { reason: r }, "Ordine annullato"); },
    rework: () => { const r = window.prompt("Motivo della rilavorazione"); if (r) run("rework", { reason: r }, "Rilavorazione avviata"); },
  };
};

export default function OrderDetail() {
  const { id } = useParams();
  const { nameOf } = useData();
  const { can } = useAuth();
  const [o, setO] = useState(null);
  const [editing, setEditing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = useActions(id, load);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("work_order_id", id);
    fd.append("kind", "documento");
    fd.append("file", file);
    await api.post("/attachments", fd).then(() => { toast.success("Allegato caricato"); load(); }).catch((err) => toast.error(errMsg(err)));
    e.target.value = "";
  };

  if (!o) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  const w = can("orders:write");
  const editable = ["RECEIVED", "TO_PLAN", "PLANNED", "SUSPENDED"].includes(o.status);

  return (
    <div data-testid="order-detail-page">
      <Link to="/ordini" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Ordini</Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 fade-up">
        <div>
          <TypeBadge type={o.service_type} />
          <h1 className="mt-2 font-mono text-4xl font-semibold tracking-tight" data-testid="order-detail-code">{o.external_order_id}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusBadge status={o.status} testId="order-detail-status" /><PriorityBadge priority={o.priority} /><SlaChip order={o} />
            {o.outcome && <span className="text-sm">Esito: <strong>{OUTCOME_LABEL[o.outcome]}</strong></span>}
            {o.attempts > 0 && <span className="font-mono text-xs text-muted-foreground">Tentativi {o.attempts} · Rilavorazioni {o.rework_count}</span>}
            {o.validated_amount != null && <span className="font-mono text-sm font-semibold text-emerald-700">{fmtEuro(o.validated_amount)}</span>}
          </div>
        </div>
        {w && (
          <div className="flex flex-wrap gap-2">
            {o.status === "RECEIVED" && <Button onClick={act.verify} data-testid="order-verify-button"><CheckCheck className="mr-1 h-4 w-4" />Verifica fattibilità</Button>}
            {editable && <Button variant="outline" onClick={() => setEditing(true)} data-testid="order-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifica</Button>}
            {["TO_PLAN", "PLANNED", "SUSPENDED"].includes(o.status) && can("planning:write") && <Button onClick={() => setScheduling(true)} data-testid="order-schedule-button"><CalendarPlus className="mr-1 h-4 w-4" />{o.scheduled_date ? "Ripianifica" : "Pianifica"}</Button>}
            {["VALIDATED", "SUSPENDED"].includes(o.status) && <Button variant="outline" onClick={act.rework} data-testid="order-rework-button"><RotateCcw className="mr-1 h-4 w-4" />Rilavorazione</Button>}
            {editable && <Button variant="ghost" onClick={act.cancel} data-testid="order-cancel-button"><Ban className="mr-1 h-4 w-4" />Annulla</Button>}
          </div>
        )}
      </div>
      <Lifecycle status={o.status} />
      <div className="mb-6 grid gap-4 md:grid-cols-5">
        <Field label="Gestore infrastruttura" value={nameOf("parties", o.infrastructure_manager_id)} />
        <Field label="Committente contrattuale" value={nameOf("parties", o.customer_id)} />
        <Field label="Commessa" value={nameOf("projects", o.project_id, "code")} mono />
        <Field label="Società titolare" value={nameOf("companies", o.contract_company_id)} />
        <Field label="Società esecutrice" value={nameOf("companies", o.executing_company_id)} />
      </div>
      <Tabs defaultValue="dati">
        <TabsList className="rounded-none border bg-white">
          <TabsTrigger value="dati" data-testid="tab-data">Dati</TabsTrigger>
          <TabsTrigger value="assegnazioni" data-testid="tab-assignments">Assegnazioni ({o.assignments.length})</TabsTrigger>
          <TabsTrigger value="rapportini" data-testid="tab-reports">Visite e rapportini ({o.reports.length})</TabsTrigger>
          <TabsTrigger value="allegati" data-testid="tab-attachments">Allegati ({o.attachments.length})</TabsTrigger>
          <TabsTrigger value="cronologia" data-testid="tab-history">Cronologia</TabsTrigger>
        </TabsList>
        <TabsContent value="dati" className="mt-4 grid gap-4 lg:grid-cols-2">
          <Section title="Cliente e indirizzo intervento">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Cliente finale" value={o.customer_name} /><Field label="Telefono" value={o.phone} mono />
              <Field label="Indirizzo" value={addressLine(o.address)} /><Field label="Dettaglio" value={addressExtra(o.address)} />
              <Field label="Regione" value={o.address.region} /><Field label="Note" value={o.notes} />
            </div>
          </Section>
          <Section title={o.service_type === "ASSURANCE" ? "Guasto" : o.service_type === "CONSTRUCTION" ? "Lavori di rete" : "Attivazione linea"}><Technical o={o} /></Section>
          <Section title="Pianificazione corrente">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Squadra" value={nameOf("teams", o.team_id)} /><Field label="Tecnico" value={nameOf("employees", o.employee_id)} />
              <Field label="Data" value={fmtDate(o.scheduled_date)} /><Field label="Fascia" value={SLOTS[o.slot]} />
              <Field label="Completato il" value={fmtDateTime(o.completed_at)} /><Field label="Sorgente" value={o.source_id === "MANUAL" ? "Inserimento manuale" : nameOf("integration_sources", o.source_id)} />
            </div>
          </Section>
          {o.production && <Economics o={o} />}
        </TabsContent>
        <TabsContent value="assegnazioni" className="mt-4"><Assignments list={o.assignments} nameOf={nameOf} /></TabsContent>
        <TabsContent value="rapportini" className="mt-4 space-y-4">
          {o.reports.length === 0 && <div className="text-sm text-muted-foreground">Nessuna visita registrata</div>}
          {o.reports.map((r) => <Section key={r.id} title={`Visita #${r.attempt_no} — ${fmtDateTime(r.created_at)}`}><ReportView report={r} /></Section>)}
        </TabsContent>
        <TabsContent value="allegati" className="mt-4">
          <Section title="Allegati (foto, verbali, test)">
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="order-attachment-input" />
            <Button variant="outline" className="mb-4" onClick={() => fileRef.current?.click()} data-testid="order-attachment-upload-button"><Upload className="mr-1 h-4 w-4" />Carica allegato</Button>
            <AttachmentGrid attachments={o.attachments} />
          </Section>
        </TabsContent>
        <TabsContent value="cronologia" className="mt-4">
          <ol className="border bg-white" data-testid="order-history">
            {o.events.map((ev) => (
              <li key={ev.id} className="grid grid-cols-[150px_110px_1fr] gap-4 border-b px-6 py-3 text-sm last:border-0">
                <span className="font-mono text-xs text-muted-foreground">{fmtDateTime(ev.at)}</span>
                <span className="micro-label">{ev.kind}</span>
                <span>{ev.text} <span className="text-muted-foreground">— {ev.by}</span></span>
              </li>
            ))}
          </ol>
        </TabsContent>
      </Tabs>
      <OrderFormDialog open={editing} onOpenChange={setEditing} order={o} onSaved={load} />
      <ScheduleDialog open={scheduling} onOpenChange={setScheduling} order={o} onDone={load} />
    </div>
  );
}
